import type { QueueEncounter, QueueStatusCounts } from '../types'

// Frontend bucket → one or more backend encounter_status values.
// Submit path: autopilot_ready/copilot_ready → writeback_queued → writeback_complete.
// "approved" is a transient state inside submitEncounter (kept for safety).
// Backend strictly derives copilot/autopilot from coding_mode (see
// queue.service.ts) — these arrays only need to carry the BE filter token.
export const FRONTEND_TO_BACKEND_MULTI: Record<string, string[]> = {
  received: ['ingested', 'predicting'],
  copilot: ['copilot_ready'],
  autopilot: ['autopilot_ready'],
  // Coding Complete = submitted by coder, awaiting / passing through writeback.
  // 'writeback_complete' is the terminal exported state — it belongs to History
  // Exported tab, NOT the Coding Complete homepage bucket.
  coding_complete: ['approved', 'writeback_queued', 'writeback_inprogress'],
  exported: ['returned'],
  // Mirrors BE EXCEPTIONS_BUCKET_STATUSES (config/buckets.ts) — includes the
  // orphan legacy 'exception' status so those charts surface in the tab.
  exceptions: ['writeback_failed', 'rejected', 'mandatory_check_failed', 'exception'],
}

export const FRONTEND_TO_BACKEND: Record<string, string> = {
  received: 'ingested',
  copilot: 'copilot_ready',
  autopilot: 'autopilot_ready',
  coding_complete: 'writeback_queued',
  exported: 'returned',
  exceptions: 'writeback_failed',
}

export const BACKEND_TO_FRONTEND: Record<string, string> = {
  ingested: 'received',
  predicting: 'received',
  copilot_ready: 'copilot',
  autopilot_ready: 'autopilot',
  approved: 'coding_complete',
  writeback_queued: 'coding_complete',
  writeback_inprogress: 'coding_complete',
  // writeback_complete = exported terminal state, lives on History tab only
  writeback_complete: 'exported',
  returned: 'exported',
  writeback_failed: 'exceptions',
  rejected: 'exceptions',
  mandatory_check_failed: 'exceptions',
  exception: 'exceptions',
}

/**
 * Bucket-scoped raw status options for the FilterDrawer Status section.
 * Keys = FE bucket slugs (filters.status[0]). Values = raw `t_enc_encounters.status`
 * enums with FE display labels. Buckets NOT present here (copilot, autopilot,
 * exported) deliberately omit the Status filter.
 */
export const STATUS_FILTER_OPTIONS_BY_BUCKET: Record<string, { value: string; label: string }[]> = {
  received: [
    { value: 'ingested', label: 'Ingested' },
    { value: 'predicting', label: 'Predicting' },
  ],
  query: [
    { value: 'pending', label: 'Pending' },
    { value: 'coding_on_hold', label: 'Coding on Hold' },
    { value: 'qa_review', label: 'Quality Check' },
  ],
  coding_complete: [
    { value: 'writeback_queued', label: 'Coding Completed' },
    { value: 'writeback_inprogress', label: 'Charge Entry Initiated' },
  ],
  exceptions: [
    { value: 'mandatory_check_failed', label: 'Mandatory Check Failed' },
    { value: 'writeback_failed', label: 'Charge Entry Warning' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'exception', label: 'Exception' },
  ],
}

/** Flat lookup: raw status enum → FE display label (only for filter-eligible statuses). */
export const STATUS_FILTER_DISPLAY_LABEL: Record<string, string> = Object.values(
  STATUS_FILTER_OPTIONS_BY_BUCKET,
).flat().reduce((acc, opt) => {
  acc[opt.value] = opt.label
  return acc
}, {} as Record<string, string>)

export function toBackendStatuses(statuses: string[]): string[] {
  const out: string[] = []
  for (const s of statuses) {
    const expanded = FRONTEND_TO_BACKEND_MULTI[s]
    if (expanded) out.push(...expanded)
    else out.push(s)
  }
  return Array.from(new Set(out))
}

/** Frontend queue bucket keys (Encounter Queue tabs). */
export type QueueBucketKey =
  | 'received'
  | 'copilot'
  | 'autopilot'
  | 'coding_complete'
  | 'exported'
  | 'exceptions'

const QUEUE_BUCKET_KEYS = new Set<string>([
  'received',
  'copilot',
  'autopilot',
  'coding_complete',
  'exported',
  'exceptions',
])

/**
 * Map raw encounter status (+ coding_mode for in-flight PE rows) to the queue
 * bucket that should list the chart. Mirrors queue.service.ts bucket SQL and
 * the CASE expression on list rows.
 */
export function resolveQueueBucket(
  status: string | null | undefined,
  codingMode?: string | null,
): QueueBucketKey | null {
  if (!status) return null
  const s = status.toLowerCase()
  if (s === 'ingested' || s === 'predicting') return 'received'
  if (s === 'copilot_ready') return 'copilot'
  if (s === 'predicted' || s === 'autopilot_ready') {
    return codingMode === 'copilot' ? 'copilot' : 'autopilot'
  }
  if (['approved', 'writeback_queued', 'writeback_inprogress', 'pending_writeback'].includes(s)) {
    return 'coding_complete'
  }
  if (s === 'returned' || s === 'writeback_complete') return 'exported'
  if (s === 'writeback_failed' || s === 'rejected' || s === 'mandatory_check_failed' || s === 'exception') return 'exceptions'
  const mapped = BACKEND_TO_FRONTEND[s]
  return mapped && QUEUE_BUCKET_KEYS.has(mapped) ? (mapped as QueueBucketKey) : null
}

const STATUS_BADGE: Record<string, { label: string; badgeClass: string }> = {
  received: { label: 'Received', badgeClass: 'status-badge' },
  copilot: { label: 'Copilot', badgeClass: 'status-badge primary' },
  autopilot: { label: 'Autopilot', badgeClass: 'status-badge info' },
  coding_complete: { label: 'Coding Completed', badgeClass: 'status-badge success' },
  exported: { label: 'Exported', badgeClass: 'status-badge success' },
  exceptions: { label: 'Exceptions', badgeClass: 'status-badge danger' },
  copilot_ready: { label: 'Copilot', badgeClass: 'status-badge primary' },
  autopilot_ready: { label: 'Autopilot', badgeClass: 'status-badge info' },
  ingested: { label: 'Received', badgeClass: 'status-badge' },
  predicting: { label: 'Predicting', badgeClass: 'status-badge warning' },
  predicted: { label: 'Processing', badgeClass: 'status-badge warning' },
  pending: { label: 'Pending', badgeClass: 'status-badge muted' },
  coding_on_hold: { label: 'Coding on hold', badgeClass: 'status-badge on-hold' },
  qa_review: { label: 'Quality check', badgeClass: 'status-badge quality-check' },
  writeback_inprogress: { label: 'Charge Entry Initiated', badgeClass: 'status-badge info' },
  writeback_failed: { label: 'Charge Entry Warning', badgeClass: 'status-badge warning' },
  mandatory_check_failed: { label: 'Mandatory Check Failed', badgeClass: 'status-badge danger' },
  rejected: { label: 'Rejected', badgeClass: 'status-badge danger' },
  exception: { label: 'Exception', badgeClass: 'status-badge danger' },
  archived: { label: 'Archived', badgeClass: 'status-badge muted' },
}

type OpenQuerySummary = NonNullable<QueueEncounter['queries']>

const PENDING_QUERY_CATEGORIES = new Set([
  'documentation',
  'clinical_clarification',
  'other',
  'system',
])
const PENDING_TEMPLATE_CODES = new Set([
  'MISSING_DOC',
  'DX_CLARIFY',
  'CODER_QUERY_OTHER',
  'DATA_IMPORT_ISSUE',
  'SYSTEM_TECHNICAL_ISSUE',
])

/** Legacy rows: copilot_ready + open query before migration backfill. */
function statusFromOpenQueries(queries: OpenQuerySummary | null | undefined): {
  label: string
  badgeClass: string
} | null {
  if (!queries?.open_count) return null
  const cats = queries.categories ?? []
  const codes = queries.template_codes ?? []

  if (cats.includes('on_hold') || codes.includes('ON_HOLD')) {
    return STATUS_BADGE.coding_on_hold
  }

  const hasPending =
    cats.some((c) => PENDING_QUERY_CATEGORIES.has(c)) ||
    codes.some((c) => PENDING_TEMPLATE_CODES.has(c))
  if (hasPending) {
    return STATUS_BADGE.pending
  }

  return null
}

/** User-facing status after a RaiseQueryPopover action — mirrors DB encounter.status. */
export const QUERY_ACTION_STATUS_LABEL: Record<string, string> = {
  md: 'Pending',
  cl: 'Pending',
  ot: 'Pending',
  di: 'Pending',
  ti: 'Pending',
  oh: 'Coding on hold',
  qc: 'Quality check',
}

const ENCOUNTER_STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  coding_on_hold: 'Coding on hold',
  qa_review: 'Quality check',
  copilot_ready: 'Copilot',
  autopilot_ready: 'Autopilot',
  writeback_inprogress: 'Charge Entry initiated',
  writeback_failed: 'Charge Entry Warning',
}

export function getQueryActionStatusLabel(actionId: string, encounterStatus?: string | null): string {
  const fromAction = QUERY_ACTION_STATUS_LABEL[actionId]
  if (fromAction) return fromAction
  if (encounterStatus && ENCOUNTER_STATUS_LABEL[encounterStatus]) {
    return ENCOUNTER_STATUS_LABEL[encounterStatus]
  }
  if (!encounterStatus) return 'Updated'
  return encounterStatus.replace(/_/g, ' ')
}

/** Status badge for queue rows and detail headers — respects coding_mode and open queries. */
export function getQueueStatusDisplay(
  status: string,
  codingMode: string | null,
  queries?: OpenQuerySummary | null,
) {
  if (status === 'pending') return STATUS_BADGE.pending
  if (status === 'coding_on_hold') return STATUS_BADGE.coding_on_hold
  if (status === 'qa_review') return STATUS_BADGE.qa_review

  const queryStatus = statusFromOpenQueries(queries)
  if (queryStatus && (status === 'copilot_ready' || status === 'autopilot_ready')) {
    return queryStatus
  }

  if (status === 'predicting') return STATUS_BADGE.predicting
  if (status === 'autopilot_ready' || status === 'predicted') {
    return codingMode === 'copilot' ? STATUS_BADGE.copilot : STATUS_BADGE.autopilot
  }
  if (status === 'writeback_inprogress') return STATUS_BADGE.writeback_inprogress
  if (status === 'writeback_failed') return STATUS_BADGE.writeback_failed
  // Exceptions-bucket rows show their OWN status label (not the generic
  // "Exceptions" bucket badge). The tab still groups them under Exceptions.
  if (status === 'mandatory_check_failed') return STATUS_BADGE.mandatory_check_failed
  if (status === 'rejected') return STATUS_BADGE.rejected
  if (status === 'exception') return STATUS_BADGE.exception

  const bucket = resolveQueueBucket(status, codingMode)
  if (bucket && STATUS_BADGE[bucket]) return STATUS_BADGE[bucket]
  return STATUS_BADGE[status] ?? { label: status, badgeClass: 'status-badge muted' }
}

export function parseQueueBucketParam(raw: string | null): QueueBucketKey | null {
  if (!raw) return null
  const key = raw.toLowerCase()
  return QUEUE_BUCKET_KEYS.has(key) ? (key as QueueBucketKey) : null
}

export function transformStatusCounts(backendCounts: Record<string, number>): QueueStatusCounts {
  return {
    received: (backendCounts.ingested ?? 0) + (backendCounts.predicting ?? 0),
    copilot: backendCounts.copilot_ready ?? 0,
    autopilot: backendCounts.autopilot_ready ?? 0,
    coding_complete:
      (backendCounts.approved ?? 0)
      + (backendCounts.writeback_queued ?? 0)
      + (backendCounts.writeback_inprogress ?? 0)
      + (backendCounts.pending_writeback ?? 0),
    exported: backendCounts.returned ?? 0,
    exceptions:
      (backendCounts.writeback_failed ?? 0)
      + (backendCounts.rejected ?? 0)
      + (backendCounts.mandatory_check_failed ?? 0)
      + (backendCounts.exception ?? 0),
  }
}

/** Open query row statuses (v1 + v2) — matches backend Query bucket / blocking checks. */
export const OPEN_ENCOUNTER_QUERY_STATUSES = new Set([
  'open',
  'responded',
  'draft',
  'awaiting_dispatch',
  'awaiting_recipient',
  'recipient_responded',
  'expired',
  'reopened',
])

/** Encounter statuses set when a coder raises a query (RaiseQueryPopover workflow). */
export const QUERY_WORKFLOW_ENCOUNTER_STATUSES = new Set([
  'pending',
  'coding_on_hold',
  'qa_review',
])

export function isQueryWorkflowEncounterStatus(status: string | null | undefined): boolean {
  return !!status && QUERY_WORKFLOW_ENCOUNTER_STATUSES.has(status)
}

/** Whether the encounter should resume active coding once all queries are closed. */
export function shouldResumeEncounterAfterQueriesCleared(
  status: string | null | undefined,
): boolean {
  if (!status) return false
  return (
    QUERY_WORKFLOW_ENCOUNTER_STATUSES.has(status)
    || status === 'autopilot_ready'
    || status === 'copilot_ready'
  )
}

/**
 * Target encounter.status after the last open query is resolved/cancelled.
 * Resumes to the chart's OWN bucket ready status (copilot charts → copilot_ready,
 * NOT autopilot_ready) — mirrors BE resumeEncounterAfterQueryWorkflowClear.
 * Optimistic fallback only; the server-returned status is authoritative.
 */
export function encounterStatusAfterQueriesCleared(
  status: string | null | undefined,
  codingMode?: string | null,
): string | null {
  if (!status || !shouldResumeEncounterAfterQueriesCleared(status)) return null
  return codingMode === 'copilot' ? 'copilot_ready' : 'autopilot_ready'
}

export function encounterHasOpenBlockingQuery(
  queries: Array<{ status: string; is_blocking?: boolean }> | null | undefined,
): boolean {
  return (queries ?? []).some(
    (q) => !!q.is_blocking && OPEN_ENCOUNTER_QUERY_STATUSES.has(q.status),
  )
}

/** Submit blocked by on-hold query while chart is in query-workflow status. */
export function isBlockingQuerySubmitHold(
  encounterStatus: string | null | undefined,
  queries: Array<{ status: string; is_blocking?: boolean }> | null | undefined,
): boolean {
  return isQueryWorkflowEncounterStatus(encounterStatus)
    && encounterHasOpenBlockingQuery(queries)
}

/** Post-submit / closed charts — read-only code review, stacked default. */
export const POST_SUBMIT_READONLY_STATUSES = new Set([
  'approved',
  'pending_writeback',
  'writeback_queued',
  'writeback_inprogress',
  'writeback_complete',
  'returned',
  'exported',
  'coding_complete',
])

export function isPostSubmitReadOnlyStatus(status: string | null | undefined): boolean {
  return !!status && POST_SUBMIT_READONLY_STATUSES.has(status)
}

/** Closed / writeback charts — right-pane first tab reads "Final Summary". */
export const FINAL_SUMMARY_TAB_STATUSES = new Set([
  'approved',
  'pending_writeback',
  'coding_complete',
  'writeback_queued',
  'writeback_inprogress',
  'writeback_complete',
])

export function isFinalSummaryTabStatus(status: string | null | undefined): boolean {
  return !!status && FINAL_SUMMARY_TAB_STATUSES.has(status)
}

export const BLOCKING_QUERY_SUBMIT_TOOLTIP =
  'Submit is disabled because this chart has a blocking query. Resolve the query before approving.'

export function isOnDemandExportRowSelectable(status: string | null | undefined): boolean {
  return status !== 'writeback_inprogress'
}
